import { useQuery } from "@tanstack/react-query";
import { useEffect, type MouseEvent } from "react";
import { api } from "../../api/client";
import { navigate } from "../../router/useRoute";
import "./landing.css";

/**
 * Trang giới thiệu ở `/` cho người chưa đăng nhập.
 *
 * Trước 09/10/2026 `/` mở thẳng màn đăng nhập: người bấm từ quảng cáo hay từ
 * trang Chrome Web Store không có chỗ nào đọc được FaceGo làm gì, giá bao nhiêu.
 *
 * Trang kể đúng ba việc người bán trên Facebook làm mỗi ngày, theo thứ tự họ
 * làm: tìm khách → đăng bài → seeding. Mỗi chặng có ảnh chụp thật của extension
 * (dữ liệu mẫu, cột SĐT đã làm mờ). Không số liệu tự đặt, không lời chứng thực,
 * không so sánh với công cụ khác; giá đọc thẳng từ bảng giá công khai của máy
 * chủ, không chép tay vào đây.
 */

/** Link Chrome Web Store. Chưa có trong repo — để trống thì nút cài không hiện. */
const CHROME_STORE_URL = "";

interface CatalogPricePoint {
  duration_days?: number | null;
  list_amount: number;
  sale_amount: number;
  term_months: number;
}

interface CatalogItem {
  name: string;
  prices: CatalogPricePoint[];
  slug: string;
}

const STAGES = [
  { id: "tim-khach", num: "01", title: "Tìm khách hàng", tools: "Quét Page · Phân tích Ads đối thủ" },
  { id: "dang-bai", num: "02", title: "Đăng bài", tools: "Đăng bài · Đặt lịch · Reup" },
  { id: "seeding", num: "03", title: "Seeding", tools: "Seeding bài viết · Tăng follow · Tăng đánh giá" },
] as const;

const FREE_TOOLS = [
  "Đăng bài lên nhiều Page cùng lúc",
  "Đặt lịch đăng bài",
  "Reup bài từ Page khác, TikTok",
  "Seeding bài viết: thích, bình luận, chia sẻ",
  "Tăng follow, tăng đánh giá",
  "Tìm Page, quét người tương tác của một bài viết",
];

/** Bảng giá 09/10/2026: chỉ FREE và Pro một tháng — Pro mở toàn bộ, kể cả mọi thứ của FREE. */
const PRO_FEATURES = [
  "Mọi công cụ của bản Miễn phí",
  "Quét Page đối thủ ra tệp khách hàng",
  "Phân tích quảng cáo đối thủ và người tương tác",
  "Xuất file Excel",
  "Không giới hạn số Page đăng cùng lúc",
];

export function LandingPage() {
  useEffect(() => {
    document.title = "FaceGo · Tìm khách, đăng bài, seeding trên Facebook";
    // `/pricing` (link trên store listing) mở đúng trang này rồi cuộn tới mục Giá.
    if (window.location.pathname.replace(/\/+$/, "") === "/pricing") document.getElementById("gia")?.scrollIntoView();
  }, []);

  const go = (path: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(path);
  };

  return (
    <div className="lp">
      <div className="lp__wrap">
        <header className="lp-nav">
          <a className="lp-nav__brand" href="/" aria-label="FaceGo — trang chủ">
            <img src="/assets/favicon.svg" alt="" width={28} height={28} />
            FaceGo
          </a>
          <div className="lp-nav__actions">
            <a className="lp-link" href="/login" onClick={go("/login")}>Đăng nhập</a>
            <a className="lp-btn lp-btn--primary" href="/register" onClick={go("/register")}>Dùng thử miễn phí</a>
          </div>
        </header>

        <main>
          <section className="lp-hero" aria-labelledby="lp-title">
            <h1 id="lp-title">Lấy khách từ đối thủ, đăng bài lên mọi Page.</h1>
            <div className="lp-hero__lede">
              <p>
                FaceGo là extension Chrome cho người bán trên Facebook: soi Page và quảng cáo của đối thủ ra tệp khách hàng,
                đăng một bài lên nhiều Page cùng lúc, và seeding cho bài có tương tác.
              </p>
              <div className="lp-hero__ctas">
                <a className="lp-btn lp-btn--primary" href="/register" onClick={go("/register")}>Tạo tài khoản dùng thử</a>
                {CHROME_STORE_URL ? <a className="lp-btn lp-btn--ghost" href={CHROME_STORE_URL} target="_blank" rel="noreferrer">Cài extension</a> : null}
                <a className="lp-btn lp-btn--ghost" href="#gia">Xem giá</a>
              </div>
              <p className="lp-hero__note">3 ngày đầu sau khi cài: dùng trọn quyền Pro, không cần thanh toán.</p>
            </div>
          </section>

          <nav aria-label="Ba việc FaceGo làm">
            <ol className="lp-stages">
              {STAGES.map((stage) => (
                <li key={stage.id}>
                  <a href={`#${stage.id}`}>
                    <span className="lp-num">{stage.num}</span>
                    <strong>{stage.title}</strong>
                    <span>{stage.tools}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <section className="lp-stage" id="tim-khach" aria-labelledby="tim-khach-title">
            <div className="lp-stage__head">
              <span className="lp-num">01</span>
              <h2 id="tim-khach-title">Tìm khách hàng từ chính đối thủ</h2>
              <p>Người đã thích, bình luận dưới bài và quảng cáo của đối thủ là người đang quan tâm đúng thứ bạn bán.</p>
            </div>

            <div className="lp-step">
              <div className="lp-step__text">
                <h3>Quét Page đối thủ <span className="lp-tag">Pro</span></h3>
                <ol>
                  <li>Dán link Page.</li>
                  <li>FaceGo đọc bài viết và người tương tác của Page đó.</li>
                  <li>Ra danh sách khách hàng kèm giới tính; quét lại chỉ lấy phần mới.</li>
                  <li>Tải Excel.</li>
                </ol>
              </div>
              <figure className="lp-shot">
                <img src="/assets/landing/page-scan.jpg" alt="Màn Quét Page: danh sách khách hàng của một Page đối thủ, bên phải là các Page đã quét" width={2274} height={1440} loading="lazy" />
                <figcaption>Ảnh chụp màn hình extension, dữ liệu mẫu.</figcaption>
              </figure>
            </div>

            <div className="lp-step">
              <div className="lp-step__text">
                <h3>Phân tích quảng cáo đối thủ <span className="lp-tag">Pro</span></h3>
                <ol>
                  <li>Dán link Page, FaceGo đọc các quảng cáo đang chạy và đã chạy bao lâu.</li>
                  <li>Tìm bài gốc của từng quảng cáo, kèm số cảm xúc và bình luận.</li>
                  <li>Tick quảng cáo muốn lấy khách, quét người tương tác thành một tệp.</li>
                </ol>
              </div>
              <figure className="lp-shot">
                <img src="/assets/landing/ads.jpg" alt="Màn Phân tích Ads: bảng quảng cáo của một Page, cột bài gốc, cảm xúc, bình luận và nút Quét tương tác" width={2274} height={1280} loading="lazy" />
                <figcaption>Ảnh chụp màn hình extension, dữ liệu mẫu.</figcaption>
              </figure>
            </div>
          </section>

          <section className="lp-stage" id="dang-bai" aria-labelledby="dang-bai-title">
            <div className="lp-stage__head">
              <span className="lp-num">02</span>
              <h2 id="dang-bai-title">Đăng một bài lên nhiều Page cùng lúc</h2>
              <p>Soạn một lần, chọn Page, Profile hay Nhóm cần đăng, rồi để FaceGo đăng lần lượt.</p>
            </div>
            <div className="lp-step">
              <div className="lp-step__text">
                <h3>Đăng bài · Đặt lịch <span className="lp-tag lp-tag--free">Miễn phí</span></h3>
                <ol>
                  <li>Hình, video, link Shopee hoặc reup bài có sẵn.</li>
                  <li>Hàng chờ nhiều bài, viết nội dung bằng AI.</li>
                  <li>Đặt lịch, hoặc reup từ Page khác và TikTok.</li>
                </ol>
              </div>
              <figure className="lp-shot">
                <img src="/assets/landing/posting.jpg" alt="Màn Đăng bài: hàng chờ bài, các loại nội dung, và danh sách Page đích bên phải" width={2274} height={1440} loading="lazy" />
                <figcaption>Ảnh chụp màn hình extension, dữ liệu mẫu.</figcaption>
              </figure>
            </div>
          </section>

          <section className="lp-stage" id="seeding" aria-labelledby="seeding-title">
            <div className="lp-stage__head">
              <span className="lp-num">03</span>
              <h2 id="seeding-title">Seeding cho bài có tương tác</h2>
              <p>Bài có người thích và bình luận trông đáng tin hơn với người xem sau.</p>
            </div>
            <div className="lp-step">
              <div className="lp-step__text">
                <h3>Seeding bài viết <span className="lp-tag lp-tag--free">Miễn phí</span></h3>
                <ol>
                  <li>Dán link bài, chọn các Page sẽ tương tác.</li>
                  <li>Bật thích, bình luận, chia sẻ.</li>
                  <li>Đặt giãn cách giữa mỗi lượt.</li>
                </ol>
              </div>
              <figure className="lp-shot">
                <img src="/assets/landing/seeding.jpg" alt="Màn Seeding bài viết: bài đã phân tích, hành động thích bình luận chia sẻ, giãn cách và danh tính bên phải" width={2274} height={1144} loading="lazy" />
                <figcaption>Ảnh chụp màn hình extension, dữ liệu mẫu.</figcaption>
              </figure>
            </div>
          </section>

          <section className="lp-stage" id="gia" aria-labelledby="gia-title">
            <div className="lp-stage__head">
              <h2 id="gia-title">Giá</h2>
              <p>Hai gói. Đăng bài và seeding miễn phí; Pro mở toàn bộ tính năng.</p>
            </div>
            <Pricing />
          </section>

          <section className="lp-close" aria-labelledby="lp-close-title">
            <h2 id="lp-close-title">Dùng trọn bộ FaceGo trong 3 ngày.</h2>
            <a className="lp-btn lp-btn--primary" href="/register" onClick={go("/register")}>Tạo tài khoản dùng thử</a>
          </section>
        </main>

        <footer className="lp-foot">
          <strong>FaceGo</strong>
          <nav aria-label="Chân trang">
            <a className="lp-link" href="/terms" onClick={go("/terms")}>Điều khoản</a>
            <a className="lp-link" href="/privacy" onClick={go("/privacy")}>Bảo mật</a>
            <a className="lp-link" href="/support" onClick={go("/support")}>Hỗ trợ</a>
            <a className="lp-link" href="/account/delete" onClick={go("/account/delete")}>Xoá tài khoản</a>
          </nav>
          <span>© 2026 FaceGo Team</span>
        </footer>
      </div>
    </div>
  );
}

/**
 * Giá Pro đọc từ `/extension/billing/catalog` — bảng giá công khai mà extension
 * cũng dùng —, không chép tay vào đây. Từ 09/10/2026 chỉ bán kỳ 1 tháng; gói thử
 * 1 tuần và kỳ 3/6/12 tháng đã ngừng bán nên không còn bày.
 */
function Pricing() {
  const catalog = useQuery({
    queryKey: ["public", "catalog"],
    queryFn: () => api<{ catalog?: { items?: CatalogItem[] } }>("/extension/billing/catalog"),
    staleTime: 5 * 60_000,
  });
  const pro = catalog.data?.catalog?.items?.find((item) => item.slug === "facego_pro");
  const monthly = pro?.prices.find((price) => price.term_months === 1);

  return (
    <div className="lp-pricing">
      <article className="lp-plan">
        <h3>Miễn phí</h3>
        <p className="lp-plan__price">0đ</p>
        <ul>{FREE_TOOLS.map((tool) => <li key={tool}>{tool}</li>)}</ul>
      </article>

      <article className="lp-plan lp-plan--pro">
        <h3>Pro</h3>
        {monthly ? (
          <p className="lp-plan__price">
            {monthly.list_amount > monthly.sale_amount ? <del>{vnd(monthly.list_amount)}</del> : null}
            {vnd(monthly.sale_amount)}<span> / tháng</span>
          </p>
        ) : (
          <p className="lp-muted">{catalog.isLoading ? "Đang tải giá…" : "Chưa tải được giá. Đăng nhập để xem giá hiện tại."}</p>
        )}
        <ul>{PRO_FEATURES.map((feature) => <li key={feature}>{feature}</li>)}</ul>
        <p className="lp-muted">3 ngày đầu sau khi cài: dùng trọn quyền Pro, không cần thanh toán.</p>
      </article>
    </div>
  );
}

function vnd(amount: number): string {
  return `${amount.toLocaleString("vi-VN")}đ`;
}
